import { useEffect, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

declare global {
   interface Window {
      google: any;
   }
}

interface UseGoogleAuthOptions {
   onSuccess: (idToken: string) => Promise<void> | void;
   onError?: (error: string) => void;
}

export function useGoogleAuth({ onSuccess, onError }: UseGoogleAuthOptions) {
   const [isGoogleReady, setIsGoogleReady] = useState(false);

   const handleGoogleSignIn = useCallback(
      async (response: any) => {
         if (!response.credential) {
            const errorMsg = 'No credential received from Google';
            if (onError) {
               onError(errorMsg);
            } else {
               toast.error(errorMsg);
            }
            return;
         }

         try {
            await onSuccess(response.credential);
         } catch (error) {
            const errorMsg = 'An unexpected error occurred';
            if (onError) {
               onError(errorMsg);
            } else {
               toast.error(errorMsg);
            }
         }
      },
      [onSuccess, onError]
   );

   useEffect(() => {
      if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
         console.warn('REACT_APP_GOOGLE_CLIENT_ID is not set');
         return;
      }

      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');

      const initializeGoogle = () => {
         if (window.google) {
            try {
               window.google.accounts.id.initialize({
                  client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
                  callback: handleGoogleSignIn,
               });
               setIsGoogleReady(true);
            } catch (error) {
               console.error('Error initializing Google auth:', error);
               setIsGoogleReady(false);
            }
         }
      };

      if (existingScript && window.google) {
         initializeGoogle();
      } else if (!existingScript) {
         const script = document.createElement('script');
         script.src = 'https://accounts.google.com/gsi/client';
         script.async = true;
         script.defer = true;
         script.onload = initializeGoogle;
         script.onerror = () => {
            console.error('Failed to load Google Identity Services script');
            setIsGoogleReady(false);
         };
         document.body.appendChild(script);
      }
   }, [handleGoogleSignIn]);

   const signInWithGoogle = useCallback(() => {
      if (!window.google?.accounts?.id) {
         const errorMsg = 'Google Sign-In is not available. Please try again.';
         if (onError) {
            onError(errorMsg);
         } else {
            toast.error(errorMsg);
         }
         return;
      }

      window.google.accounts.id.prompt();
   }, [onError]);

   return {
      signInWithGoogle,
      isGoogleReady,
   };
}

